import { MediaRef } from '$/GraphQL/schema/types';
import { fixedFloat } from './number';
import { isFromDomain } from './string';

export const isImageUrl = async (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = (): void => resolve(true);
    img.onerror = (): void => resolve(false);
    img.src = url;
  });
};

export const base64ToFile = async (base64Url: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    void (async function () {
      const res = await isImageUrl(base64Url);
      if (res) {
        try {
          fetch(base64Url)
            .then((response) => {
              response.arrayBuffer().then(function (buffer) {
                const file = new Blob([buffer]) as File;
                resolve(file);
              });
            })
            .catch((err) => {
              reject(err.toString());
            });
        } catch (e) {
          const error = e as Error;
          reject(String(error));
        }
      }
    })();
  });
};

export const urlToFile = async (url: string): Promise<File> => {
  const response = await fetch(url);
  const blob = await response.blob();
  const contentType = blob.type || response.headers.get('Content-Type') || 'image/jpeg';
  const filename = url.split('/').pop() || 'upload';
  return new File([blob], filename, { type: contentType });
};

export const getOriginalSizeImage = async (
  url: string
): Promise<{
  height: number;
  width: number;
}> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = (): void => resolve({ height: img.height, width: img.width });
    img.onerror = (): void => resolve({ height: 1, width: 1 });
    img.src = url;
  });
};

export function cssDimensionsForRef(
  ref: MediaRef | undefined | null,
  css: { maxWidth: number; maxHeight?: number }
): string {
  let dimensions = '';

  if (ref && ref.width && ref.height && css.maxHeight && ref.fit === 'contain') {
    const ratio = ref.width / ref.height;
    let height;
    let width;
    if (ratio > 1) {
      // landscape
      width = Math.min(css.maxWidth, ref.width);
      height = width / ratio;
    } else {
      // portrait
      height = Math.min(css.maxHeight ?? 10000000000000, ref.height);
      width = height * ratio;
    }
    dimensions = `width: ${width}px; height: ${height}px;`;
  }

  return `
    max-width: ${css.maxWidth ? `${css.maxWidth}px` : 'undefined'};
    max-height: ${css.maxHeight ? `${css.maxHeight}px` : 'undefined'};
    ${dimensions}
  `;
}

/**
 * Get the CSS by Crop values for a data:… image or external image
 * @param ref - The MediaRef to get the CSS for
 * @returns The CSS for the image
 */
export function getCssImagePreviewByCrop(ref: MediaRef | undefined | null): string {
  const { crop, imageUrl } = ref ?? {};
  const isExternalUrl =
    imageUrl && !isFromDomain(imageUrl, 'imgix.net') && !isFromDomain(imageUrl, 'unsplash.com');
  const isDataImage = imageUrl?.startsWith('data:') || (crop && isExternalUrl);
  if (isDataImage && crop) {
    const zoomLevel = fixedFloat(Math.min(1 / crop[2], 1 / crop[3]));
    return `
            background-position: ${(crop[0] / (1 - crop[2])) * 100}%
              ${(crop[1] / (1 - crop[3])) * 100}%;
            background-size: ${zoomLevel === 1 ? 'cover' : `${fixedFloat(1 / crop[2]) * 100}%`};
          `;
  }
  return '';
}
